import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";

export default function PrivacyPolicy() {
  const [theme, setTheme] = useState("light");

  useEffect(() => {
    const mq = window.matchMedia("(prefers-color-scheme: dark)");
    setTheme(mq.matches ? "dark" : "light");
    mq.addEventListener("change", (e) =>
      setTheme(e.matches ? "dark" : "light"),
    );
    return () => mq.removeEventListener("change", () => {});
  }, []);

  return (
    <div
      className={`min-h-screen w-full transition-colors duration-500 font-sans ${
        theme === "dark"
          ? "bg-[#0d0d0d] text-gray-200"
          : "bg-gray-50 text-gray-900"
      }`}
    >
      {/* Hero Section */}
      <div className="relative bg-gradient-to-r from-emerald-600 via-cyan-500 to-blue-600 text-white py-20 md:py-28 overflow-hidden">
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease: "easeOut" }}
          className="relative z-10 max-w-5xl mx-auto px-6 text-center"
        >
          <h1 className="text-5xl md:text-6xl font-extrabold tracking-tight drop-shadow-lg">
            Privacy Policy
          </h1>
          <p className="mt-4 text-lg md:text-xl opacity-90">
            Last updated: August 21, 2025
          </p>
        </motion.div>
        <div className="absolute inset-0 bg-black/20 backdrop-blur-2xl"></div>
      </div>

      {/* Content */}
      <motion.div
        initial={{ opacity: 0, y: 50 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.7, ease: "easeOut" }}
        className="relative -mt-12 md:-mt-16 max-w-5xl mx-auto px-6"
      >
        <div className="rounded-3xl bg-white/90 dark:bg-zinc-900/90 shadow-2xl p-8 md:p-12 backdrop-blur-lg">
          <main className="prose prose-lg dark:prose-invert max-w-none leading-relaxed">
            <p>
              This Privacy Policy describes Our policies and procedures on the
              collection, use and disclosure of Your information when You use
              the Service and tells You about Your privacy rights and how the
              law protects You.
            </p>
            <p>
              We use Your Personal data to provide and improve the Service. By
              using the Service, You agree to the collection and use of
              information in accordance with this Privacy Policy.
            </p>

            <div className="my-10 h-[2px] bg-gradient-to-r from-emerald-400 via-cyan-400 to-blue-500 rounded-full"></div>

            <h2 className="text-3xl font-bold">
              Interpretation and Definitions
            </h2>
            <h3 className="text-2xl font-semibold">Interpretation</h3>
            <p>
              The words of which the initial letter is capitalized have meanings
              defined under the following conditions. The definitions shall have
              the same meaning regardless of whether they appear in singular or
              in plural.
            </p>

            <h3 className="text-2xl font-semibold">Definitions</h3>
            <ul className="list-disc pl-6 space-y-2">
              <li>
                <b>Account</b>: a unique account created for You to access our
                Service.
              </li>
              <li>
                <b>Affiliate</b>: an entity under common control with a party.
              </li>
              <li>
                <b>Company</b>: ParkingSpots LLC, 3447 Renner Rd, #120 Plano, TX
                75074.
              </li>
              <li>
                <b>Cookies</b>: small files placed on your device.
              </li>
              <li>
                <b>Country</b>: Texas, United States.
              </li>
              <li>
                <b>Device</b>: any device that can access the Service.
              </li>
              <li>
                <b>Personal Data</b>: any information that relates to an
                identifiable individual.
              </li>
              <li>
                <b>Service</b>: the Website.
              </li>
              <li>
                <b>Website</b>:{" "}
                <a
                  href="https://www.Parkingspotsllc.com"
                  target="_blank"
                  rel="noreferrer"
                  className="text-emerald-600 dark:text-emerald-400 font-medium underline underline-offset-2 hover:text-emerald-500"
                >
                  www.Parkingspotsllc.com
                </a>
              </li>
            </ul>

            <div className="my-10 h-[2px] bg-gradient-to-r from-emerald-400 via-cyan-400 to-blue-500 rounded-full"></div>

            <h2 className="text-3xl font-bold">
              Collecting and Using Your Personal Data
            </h2>
            <h3 className="text-2xl font-semibold">Types of Data Collected</h3>

            <h4 className="text-xl font-semibold">Personal Data</h4>
            <p>
              While using Our Service, We may ask You to provide personally
              identifiable information, including but not limited to:
            </p>
            <ul className="list-disc pl-6">
              <li>Email address</li>
              <li>First and last name</li>
              <li>Phone number</li>
              <li>Usage Data</li>
            </ul>

            <h4 className="text-xl font-semibold mt-6">Usage Data</h4>
            <p>
              Usage Data is collected automatically when using the Service and
              may include device, browser, IP address, and session details.
            </p>

            <h4 className="text-xl font-semibold mt-6">
              Tracking Technologies
            </h4>
            <p>
              We use Cookies, web beacons, and similar technologies to improve
              and analyze our Service. You may disable Cookies in your browser
              settings.
            </p>

            <div className="my-10 h-[2px] bg-gradient-to-r from-emerald-400 via-cyan-400 to-blue-500 rounded-full"></div>

            <h2 className="text-3xl font-bold">Use of Your Personal Data</h2>
            <ul className="list-disc pl-6 space-y-2">
              <li>To provide and maintain our Service</li>
              <li>To manage Your Account</li>
              <li>To perform contracts</li>
              <li>To contact You (email, SMS, calls, push notifications)</li>
              <li>To provide offers and general information</li>
              <li>To manage requests</li>
              <li>For business transfers</li>
              <li>For analytics and improvements</li>
            </ul>
            <p className="mt-4 font-semibold text-red-600 dark:text-red-400">
              We will not share your mobile opt-in data with third parties or
              affiliates.
            </p>

            <div className="my-10 h-[2px] bg-gradient-to-r from-emerald-400 via-cyan-400 to-blue-500 rounded-full"></div>

            <h2 className="text-3xl font-bold">Contact Us</h2>
            <p>
              If you have any questions about this Privacy Policy, you can
              contact us:
            </p>
            <ul className="list-disc pl-6 space-y-2">
              <li>
                Email:{" "}
                <a
                  href="mailto:info@Parkingspotsllc.com"
                  className="text-emerald-600 hover:text-emerald-500 underline"
                >
                  info@Parkingspotsllc.com
                </a>
              </li>
              <li>
                Website:{" "}
                <a
                  href="https://www.Parkingspotsllc.com/#contact-panel"
                  className="text-emerald-600 hover:text-emerald-500 underline"
                >
                  www.Parkingspotsllc.com/#contact-panel
                </a>
              </li>
              <li>
                Phone:{" "}
                <a
                  href="tel:+18885025242"
                  className="text-emerald-600 hover:text-emerald-500 underline"
                >
                  +1 (888) 502-5242
                </a>
              </li>
            </ul>
          </main>
        </div>

        {/* Back button */}
        <div className="mt-16 flex justify-center">
          <Link
            to="/"
            className="group inline-flex items-center gap-3 px-8 py-3 rounded-full bg-gradient-to-r from-emerald-600 via-cyan-500 to-blue-600 text-white font-semibold shadow-lg hover:shadow-xl hover:scale-105 transition-transform duration-300"
          >
            <ArrowLeft className="w-5 h-5 transition-transform group-hover:-translate-x-1" />
            Back to Home
          </Link>
        </div>
      </motion.div>
    </div>
  );
}
